import { omit, uniqueId } from "lodash-es";
import { FC, useEffect, useMemo, useState } from "react";

import {
  EventTravelLegType,
  ITravelBlockBookingTicketParams,
  ITravelBlockPassenger,
  ITravelBlockPassengerParams,
  TravelLegType,
} from "Bands/types";
import {
  getTravelLegApiEndpoint,
  getTravelLegTitle,
  usePagination,
} from "Bands/utils";
import { FormBuilder } from "shared/components";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIError, IAPIState, IFormButtons } from "shared/types";
import { getIdFromPath } from "shared/utils";

import { getFormFields } from "./formData";
import { travelBlockPassengersSelectorCreator } from "Bands/state";
import { useSelector } from "react-redux";

interface Props {
  bandId: string;
  blockId: string;
  travelLegs: EventTravelLegType[];
  apiStatus?: IAPIState;
  error?: boolean | string;
  passenger?: ITravelBlockPassenger;
  buttons?: IFormButtons;
  resetForm?: boolean;
  apiError?: IAPIError | null;
  excludeOptions?: string[] | undefined;
  handleSubmit: (data: ITravelBlockPassengerParams) => void;
  disableUser?: boolean;
}
export interface TravelLegField {
  id: string;
  travelLegId?: string;
}

export const TravelBlockPassengerForm: FC<Props> = ({
  bandId,
  blockId,
  apiStatus,
  error,
  buttons,
  handleSubmit,
  passenger,
  resetForm,
  apiError,
  travelLegs,
  disableUser,
}) => {
  const [travelLegFields, setTravelLegFields] = useState<TravelLegField[]>([]);

  const pagination = usePagination(API_ENDPOINTS.EVENT_TRAVEL_BLOCK(blockId));

  const passengersSelector = useMemo(() => {
    const { pages, currentPage } = pagination;

    return travelBlockPassengersSelectorCreator(pages?.[currentPage]);
  }, [pagination]);

  const passengers = useSelector(passengersSelector);

  useEffect(() => {
    passenger?.bookingTickets.forEach(
      ({ travelLeg: { id: travelLegPath, type } }) => {
        const travelLegId = getIdFromPath(
          travelLegPath,
          getTravelLegApiEndpoint(type as TravelLegType).list
        );

        setTravelLegFields((prev: TravelLegField[]) => [
          ...prev,
          { id: travelLegPath, travelLegId: travelLegId },
        ]);
      }
    );
  }, [passenger?.bookingTickets]);

  const options: ISelectOption[] = useMemo(
    () =>
      travelLegs.map(({ id, ...rest }, index) => {
        const { title, prefix } = getTravelLegTitle(
          omit(rest, ["departure", "arrival"]),
          index + 1
        );

        return {
          label: prefix || id,
          suffix: prefix === title ? undefined : `(${title || id})`,
          value: id,
        };
      }),
    [travelLegs]
  );

  const addTravelLeg = () => {
    if (travelLegFields.length >= travelLegs.length) return;

    setTravelLegFields((prev) => [...prev, { id: uniqueId() }]);
  };

  const onSubmit = (data: any) => {
    const { bookingReference, files } = data;

    const cleanData: ITravelBlockPassengerParams = {
      user: API_ENDPOINTS.BAND_USER(data.user || ""),
      travelBlock: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(blockId),
      bookingTickets: [],
    };

    travelLegFields.forEach(({ id: fieldId, travelLegId: tId }) => {
      const travelLegType = travelLegs.find(
        ({ id: travelLegId }) => travelLegId === tId
      )?.type;

      if (!travelLegType) return;

      let bookingTicket: ITravelBlockBookingTicketParams = {
        travelLeg: getTravelLegApiEndpoint(travelLegType as TravelLegType).item(
          tId as string
        ),
      };

      bookingTicket.bookingReference = bookingReference[fieldId];

      if (defaultValues?.[`files.${fieldId}`] && files?.[fieldId]) {
        bookingTicket.files = [
          `/api/files/${defaultValues[`files.${fieldId}`].id}`,
        ];
      }

      const file = files?.[fieldId]?.file;

      if (file) {
        bookingTicket.files = [{ ...file, band: API_ENDPOINTS.BAND(bandId) }];
      }

      cleanData.bookingTickets?.push(bookingTicket);
    });

    handleSubmit(cleanData);
  };

  const defaultValues = useMemo(() => {
    if (!passenger) return undefined;

    const formValues: any = {};

    formValues.user = passenger.user.id;

    passenger.bookingTickets.forEach(
      ({ travelLeg, bookingReference, files }) => {
        formValues[`travelLeg.${travelLeg.id}`] = getIdFromPath(
          travelLeg.id,
          getTravelLegApiEndpoint(travelLeg.type as TravelLegType).list
        );

        formValues[`bookingReference.${travelLeg.id}`] = bookingReference;

        if (files[0]?.source) {
          formValues[`files.${travelLeg.id}`] = {
            id: files[0].id,
            originalURL: files[0].source,
          };
        }
      }
    );

    return formValues;
  }, [passenger]);

  return (
    <FormBuilder
      isSubmitting={apiStatus === IAPIState.pending}
      apiError={apiError}
      apiErrorMessage={error}
      fields={getFormFields({
        travelLegFields,
        setTravelLegFields,
        disableUser,
        options,
        addTravelLeg,
        isAddButtonDisabled: travelLegFields.length >= travelLegs.length,
        excludeUserIds: disableUser
          ? []
          : passengers.map(({ user }) => user.id),
      })}
      onSubmit={onSubmit}
      buttons={buttons}
      defaultValues={defaultValues}
      resetForm={resetForm}
      resetFormOnSubmit={false}
      style={{
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        mobile: {
          gridTemplateColumns: "1fr",
        },
      }}
    />
  );
};
